/* eslint-disable react/prop-types */
import React, { useState, useRef, useEffect } from "react";
import { Button } from "@nextui-org/button";
import { ReactComponent as ChevronDown } from "../../../assets/icons/chevron-down.svg";
import { Input } from "@nextui-org/input";
import { ReactComponent as Search } from "../../../assets/icons/search.svg";
import { Checkbox } from "@nextui-org/react";
import { useDropdown } from "./FilterContext";

const SearchInput = ({ onChange }) => {
  return (
    <Input
      className="w-full"
      variant="flat"
      onClick={(e) => e.stopPropagation()}
      onFocus={(e) => e.stopPropagation()}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
      }}
      classNames={{
        base: "border-none focus:border-none",
        input: "border-none focus:border-none",
        innerWrapper: "border-none !bg-white",
        inputWrapper: [
          "border-none",
          "shadow-none",
          "!bg-white",
          "data-[hover=true]:!bg-white",
          "group-data-[focus=true]:!bg-white",
          "group-data-[focus=true]:ring-0",
          "group-data-[focus=true]:ring-offset-0",
        ],
      }}
      startContent={<Search />}
    />
  );
};

const FilterSelector = ({
  id,
  label,
  options,
  selectedValue,
  onChange,
  isDisabled,
  buttonWidth = "lg:w-fit",
  searchAble = false,
  isMultiSelect = false,
  showSelectAll = false,
  selectAllLabel = "เลือกทั้งหมด",
  isNested = false,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const dropdownRef = useRef(null);
  const {
    activeDropdownId,
    toggleDropdown,
    closeDropdown,
    nestedDropdownId,
    toggleNestedDropdown,
    closeNestedDropdown,
  } = useDropdown();

  const isOpen = isNested
    ? nestedDropdownId === id && activeDropdownId === "otherFilter"
    : activeDropdownId === id;

  const handleToggle = () => {
    if (isDisabled) return;

    if (isNested) {
      toggleNestedDropdown(id);
    } else {
      toggleDropdown(id);
    }
  };

  const handleClose = () => {
    if (isNested) {
      closeNestedDropdown();
    } else {
      closeDropdown();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        handleClose();
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen, handleClose]);

  const getButtonText = () => {
    if (!isMultiSelect) {
      return `${label} ${
        selectedValue && selectedValue.name ? selectedValue.name : ""
      }`;
    }

    return label;
  };

  const isOptionSelected = (option) => {
    if (!selectedValue) return false;

    if (isMultiSelect) {
      return (
        Array.isArray(selectedValue) &&
        selectedValue.some(
          (item) =>
            item.id === option.id &&
            item.name === option.name &&
            (item._phase === option._phase || (!item._phase && !option._phase))
        )
      );
    }

    return (
      selectedValue.id === option.id &&
      selectedValue.name === option.name &&
      (selectedValue._phase === option._phase ||
        (!selectedValue._phase && !option._phase))
    );
  };

  const filteredOptions =
    options?.length && Array.isArray(options)
      ? options.filter((item) => {
          if (!searchValue) return true;
          return item.name.toLowerCase().includes(searchValue.toLowerCase());
        })
      : [];

  const handleItemClick = (option) => {
    if (!isMultiSelect && showSelectAll) {
      if (isOptionSelected(option)) {
        onChange(null);
      } else {
        onChange(option);
      }
      handleClose();
    } else {
      onChange(option);
      if (!isMultiSelect) {
        handleClose();
      }
    }
  };

  const handleSelectAll = () => {
    if (isMultiSelect) {
      if (!filteredOptions || filteredOptions.length === 0) return;

      const allSelected = filteredOptions.every((option) =>
        isOptionSelected(option)
      );

      if (allSelected) {
        onChange({ isSelectAll: true, selectNone: true });
      } else {
        onChange({ isSelectAll: true, options: filteredOptions });
      }
    } else if (showSelectAll) {
      onChange(null);
      handleClose();
    }
  };

  const areAllOptionsSelected = () => {
    if (!isMultiSelect || !filteredOptions || filteredOptions.length === 0)
      return false;

    return filteredOptions.every((option) => isOptionSelected(option));
  };

  const isSelectAllSelected = () => {
    if (isMultiSelect) {
      return areAllOptionsSelected();
    } else if (showSelectAll) {
      return !selectedValue;
    }
    return false;
  };

  const renderOptionsList = (options) => (
    <div className="flex flex-col gap-1">
      {(isMultiSelect || showSelectAll) && options.length > 0 && (
        <div
          className="py-2 px-3 hover:bg-gray-100 cursor-pointer rounded-md"
          onClick={handleSelectAll}
        >
          <div className="flex items-center gap-2">
            <Checkbox
              isSelected={isSelectAllSelected()}
              className="pointer-events-none"
            >
              <span>{selectAllLabel}</span>
            </Checkbox>
          </div>
        </div>
      )}
      {!searchAble && (isMultiSelect || showSelectAll) && (
        <div className="h-px w-full bg-black my-1 sticky top-0" />
      )}
      {options.map((option, index) => (
        <div
          key={`${option.id}_${option.name}_${index}`}
          className="py-2 px-3 hover:bg-gray-100 cursor-pointer rounded-md"
          onClick={() => handleItemClick(option)}
        >
          <div className="flex items-center gap-2">
            <Checkbox
              isSelected={isOptionSelected(option)}
              className="pointer-events-none"
            >
              {option.name}
            </Checkbox>
          </div>
        </div>
      ))}
    </div>
  );

  const noOptionsMessage = (
    <div className="flex flex-col items-center py-4">
      <p>ไม่พบตัวเลือก</p>
    </div>
  );

  const renderFilterContent = () => {
    return filteredOptions?.length && Array.isArray(filteredOptions)
      ? renderOptionsList(filteredOptions)
      : noOptionsMessage;
  };

  return (
    <div
      className="flex flex-row gap-x-4 items-center relative"
      ref={dropdownRef}
    >
      <Button
        className={`bg-white text-left truncate ${buttonWidth} border border-gray-300 w-full`}
        endContent={
          <div className="flex items-center gap-2">
            {isMultiSelect && selectedValue?.length ? (
              <div className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-xs">
                <span>{selectedValue?.length}</span>
              </div>
            ) : null}
            <ChevronDown
              width={10}
              className={`transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </div>
        }
        isDisabled={isDisabled}
        onPress={handleToggle}
        style={{
          justifyContent: "space-between",
          padding: "0.5rem 1rem",
        }}
      >
        {getButtonText()}
      </Button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white rounded-xl shadow-lg border border-gray-200 w-full min-w-[200px]">
          <div className="text-black max-h-80 overflow-auto p-1">
            {searchAble && (
              <div className="sticky -top-1.5 z-20 bg-white rounded-xl">
                <SearchInput onChange={setSearchValue} />
                <div className="h-px w-full bg-black my-1 sticky top-0"></div>
              </div>
            )}
            {renderFilterContent()}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterSelector;
